import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ProductSummary } from "../../api/catalog";
import { Tag } from "../../components/primitives.jsx";
import Ic from "../../components/icons.jsx";
import { prettyIndustry, skuIsCode, splitIndustry } from "./productAttrs";
import { formatMoney } from "../../lib/format";

/** Read a product attribute as a display string ("" when missing). */
export function attr(p: ProductSummary, key: string): string {
  const v = (p.attributes as Record<string, unknown> | undefined)?.[key];
  return v == null ? "" : String(v);
}

/** The comma-joined `industry` attribute, split into individual slugs. */
function industries(p: ProductSummary): string[] {
  return splitIndustry(attr(p, "industry"));
}

/**
 * The standard catalog product card — image (or a generic placeholder), key
 * specs (protein/fat/industry) and the Datasheet / Order actions. Shared by
 * the catalog list and the category page so every catalog listing looks and
 * behaves the same. The image is the product's own; `imageUrl` is its
 * category's, shown when the product has none.
 */
export function ProductCard({
  p,
  imageUrl,
  onAddToOrder,
  onDatasheet,
  canOrder,
  ordered,
}: {
  p: ProductSummary;
  imageUrl: string | null;
  onAddToOrder: () => void;
  onDatasheet: () => void;
  canOrder: boolean;
  ordered: boolean;
}) {
  const { t } = useTranslation();
  const protein = attr(p, "protein");
  const fat = attr(p, "fat");
  const industryTags = industries(p);
  const picture = p.imageUrl ?? imageUrl;
  const href = p.categorySlug ? `/catalog/${p.categorySlug}/${encodeURIComponent(p.sku)}` : null;
  const inner = (
    <>
      <div className="draw">
        {picture ? (
          <img src={picture} alt={p.name} loading="lazy" />
        ) : (
          <div style={placeholderStyle}>
            <Ic.box />
          </div>
        )}
      </div>
      <div className="meta">
        {skuIsCode(p.sku) ? (
          <>
            <span className="sku">{p.sku}</span>
            <span className="nm">{p.name}</span>
          </>
        ) : (
          <>
            <span style={skuTitle}>{p.sku}</span>
            <span style={descSub}>{p.name}</span>
          </>
        )}
      </div>
      {/* A premix or a feed carries neither value, so it gets no row of dashes. */}
      {(protein || fat) && (
        <div className="specs">
          <div><span>{t("attr.protein", "Protein")}</span><b>{protein || "—"}</b></div>
          <div><span>{t("attr.fat", "Fat")}</span><b>{fat || "—"}</b></div>
        </div>
      )}
      {industryTags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {industryTags.map((slug) => (
            <Tag key={slug} kind="neutral" dot={undefined} style={industryTagStyle}>
              {prettyIndustry(slug, t)}
            </Tag>
          ))}
        </div>
      )}
      {p.listPrice != null && (
        <div className="specs" style={{ borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: 8 }}>
          <div>
            <span>{t("catalog.price", "Price")}</span>
            <b>{formatMoney(p.listPrice, p.currency)}</b>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <button
          type="button"
          className="btn sm"
          style={{ flex: 1 }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDatasheet(); }}
          title={t("catalog.openDatasheet", "Open product datasheet")}
        >
          <Ic.eye /> {t("catalog.datasheet", "Datasheet")}
        </button>
        <button
          type="button"
          className="btn sm"
          style={ordered ? { flex: 1, background: "var(--ok-bg)", borderColor: "var(--ok)", color: "var(--ok)" } : { flex: 1 }}
          disabled={!canOrder}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddToOrder(); }}
          title={ordered
            ? t("catalog.inOrderTitle", "In your draft order — click to add another")
            : canOrder
              ? t("catalog.addToOrderTitle", "Add to current draft order")
              : t("catalog.cantOrderTitle", "Your role can't create orders")}
        >
          {ordered ? <><Ic.check /> {t("catalog.inOrder", "In order")}</> : <><Ic.plus /> {t("catalog.order", "Order")}</>}
        </button>
      </div>
    </>
  );
  const ringStyle: React.CSSProperties = ordered ? { boxShadow: "inset 0 0 0 1.5px var(--ok)" } : {};
  if (!href) return <div className="product-card" style={{ cursor: "default", ...ringStyle }}>{inner}</div>;
  return (
    <Link to={href} className="product-card" style={{ textDecoration: "none", color: "inherit", cursor: "pointer", ...ringStyle }}>
      {inner}
    </Link>
  );
}

const placeholderStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--ink-4)",
};

const industryTagStyle: React.CSSProperties = { fontSize: 10, padding: "2px 8px" };

// A SKU that is a name itself leads; the descriptive translation name is the subtitle beneath it.
const skuTitle: React.CSSProperties = { fontSize: 15.5, fontWeight: 700, color: "var(--brand-ink)", letterSpacing: "-0.01em" };
const descSub: React.CSSProperties = { fontSize: 12, color: "var(--ink-3)", lineHeight: 1.35 };
